import { CalendarOff } from "lucide-react";
import { Skeleton } from "@somethings-ui/web";
import {
  useCancelTimeOff,
  useCreateTimeOff,
  useDeleteTimeOff,
  useMentorSchedule,
  useSaveSchedule,
  useUpdateTimeOff,
} from "@/lib/api/mentor-schedule";
import {
  currentBanner,
  draftInstants,
  type ScheduleDay,
  type TimeOffDraft,
} from "@/lib/schedule/schedule-display";
import { getDeviceTimezone } from "@/lib/utils/timezone";
import { ActiveHoursCard } from "./active-hours-card";
import { TimeOffCard } from "./time-off-card";

interface SchedulePageProps {
  userId?: string;
  disabled?: boolean;
}

function CurrentTimeOffBanner({ message }: { message: string }) {
  const splitAt = message.indexOf(". ");
  const lead = splitAt === -1 ? message : message.slice(0, splitAt + 1);
  const rest = splitAt === -1 ? "" : message.slice(splitAt + 1);

  return (
    <div className="flex items-start gap-3 rounded-2xl border border-brand-apricot bg-brand-apricot/20 px-4 py-3.5 font-body text-body-2 text-brand-apricot">
      <CalendarOff className="mt-0.5 size-5 shrink-0" />
      <p>
        <span className="font-heading">{lead}</span>
        {rest ? ` ${rest}` : null}
      </p>
    </div>
  );
}

export function SchedulePage({ userId, disabled }: SchedulePageProps) {
  const {
    data: schedule,
    isLoading,
    isError,
    refetch,
  } = useMentorSchedule(userId);
  const saveSchedule = useSaveSchedule(userId);
  const createTimeOff = useCreateTimeOff(userId);
  const updateTimeOff = useUpdateTimeOff(userId);
  const deleteTimeOff = useDeleteTimeOff(userId);
  const cancelTimeOff = useCancelTimeOff(userId);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-72 w-full rounded-2xl" />
        <Skeleton className="h-[28rem] w-full rounded-2xl" />
      </div>
    );
  }

  if (isError || !schedule) {
    return (
      <div className="flex flex-col items-start gap-2 rounded-2xl border border-buttonBorder-tertiary bg-surfaces-z0 p-6">
        <p className="font-body text-body-2 text-text-primary">
          Could not load this schedule.
        </p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="font-body text-body-3 font-medium text-text-primary underline underline-offset-2"
        >
          Try again
        </button>
      </div>
    );
  }

  const current = schedule.timeOff.current[0];
  const timezone = userId ? schedule.localTimezone : getDeviceTimezone();

  const saveHours = (hours: ScheduleDay[], zone: string) =>
    saveSchedule.mutateAsync({ schedule: hours, timezone: zone });

  const saveTimeOff = async (draft: TimeOffDraft, editingId?: string) => {
    const window = draftInstants(draft, timezone);
    if (!window) return;
    const body = {
      type: draft.type,
      note: draft.note.trim() ? draft.note.trim() : null,
      startsAt: window.start.toUTC().toISO() ?? "",
      endsAt: window.end.toUTC().toISO() ?? "",
      timezone,
    };
    if (editingId) {
      await updateTimeOff.mutateAsync({ ...body, id: editingId });
      return;
    }
    await createTimeOff.mutateAsync(body);
  };

  return (
    <div className="flex flex-col gap-5">
      {current && (
        <CurrentTimeOffBanner message={currentBanner(current, timezone)} />
      )}
      <TimeOffCard
        timezone={timezone}
        current={schedule.timeOff.current}
        upcoming={schedule.timeOff.upcoming}
        past={schedule.timeOff.past}
        disabled={disabled}
        pending={createTimeOff.isPending || updateTimeOff.isPending}
        onSave={saveTimeOff}
        onDelete={(id) => deleteTimeOff.mutateAsync(id)}
        onCancelCurrent={(id) => cancelTimeOff.mutateAsync(id)}
      />
      <ActiveHoursCard
        savedTimezone={schedule.localTimezone}
        timezone={timezone}
        fromDevice={!userId}
        hours={schedule.hours}
        groups={schedule.billableGroups}
        disabled={disabled}
        pending={saveSchedule.isPending}
        onSaveHours={saveHours}
      />
    </div>
  );
}
